import { BoltIcon } from "@heroicons/react/24/solid";
import moment from "jalali-moment";
import { WalletIcon } from "@heroicons/react/24/outline";
import { Wallet, Gauge, CalendarClock, ShoppingCart, Zap, Coins, CalendarDays } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ColorRing } from "react-loader-spinner";
import { useTranslation } from "react-i18next";

export default function StatisticsCards({
    planName,
    wallet,
    remainingTokens,
    planTokens,
    endDay,
    startDay,
    botInfoLoading,
}) {
    const { t, i18n } = useTranslation(["user"]);

    const navigate = useNavigate();
    const now = moment();
    const end = moment(endDay, moment.ISO_8601);
    const start = moment(startDay, moment.ISO_8601);
    const duration = Math.max(end.diff(start, "days"), 0);
    const remainingDays = Math.max(end.diff(now, "days"), 0);
    const isEnglish = (i18n.language === "en");

    let daysCompletion;
    let safeRemainingDays;

    if (duration === 0 || isNaN(duration) || !isFinite(duration)) {
        safeRemainingDays = t("statisticsCards.unlimited");
        daysCompletion = 100;
    } else {
        safeRemainingDays = remainingDays;
        daysCompletion = Math.min(((remainingDays / duration) * 100).toFixed(1), 100);
    }

    const statisticsCardsData = [
        {
            name: t("statisticsCards.remainingVolume"),
            icon: Gauge,
            value: remainingTokens,
            unit: t("statisticsCards.token"),
            completion: Math.min(((remainingTokens / planTokens) * 100).toFixed(1), 100),
        },
        {
            name: t("statisticsCards.remainingDays"),
            icon: CalendarClock,
            value: safeRemainingDays,
            unit: t("statisticsCards.day"),
            completion: daysCompletion,
        },
    ];

    const Loader = () => (
        <ColorRing
            visible={true}
            height="30"
            width="30"
            ariaLabel="loading"
            colors={["#0F766E", "#14B8A6", "#5EEAD4", "#FCD34D", "#FBBF24"]}
        />
    );

    /* ---------- progress color ---------- */
    const progressClass = (val) => {
        if (val === 100) return "from-emerald-400 to-teal-400 shadow-[0_0_10px_rgba(16,185,129,0.6)]";
        if (val < 40) return "from-amber-400 to-amber-500 shadow-[0_0_10px_rgba(251,191,36,0.6)]";
        return "from-teal-400 to-amber-300 shadow-[0_0_10px_rgba(45,212,191,0.6)]";
    };

    /* ---------- reusable card shell ---------- */
    const CardShell = ({ children, className = "" }) => (
        <div
            className={`
                group relative overflow-hidden rounded-2xl
                bg-white/70 dark:!bg-slate-900/70
                backdrop-blur-xl
                border border-teal-500/15 dark:!border-teal-400/15
                shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)]
                dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]
                transition-all duration-300 ease-out
                hover:-translate-y-0.5
                hover:shadow-[0_18px_50px_-20px_rgba(15,118,110,0.6)]
                ${className}
            `}
        >
            {/* Gold top accent */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
            {/* hover glow */}
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl" />
            {children}
        </div>
    );

    /* ---------- reusable icon box ---------- */
    const IconBox = ({ icon: Icon, tone = "teal" }) => {
        const toneClass =
            tone === "gold"
                ? "bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_6px_20px_-6px_rgba(251,191,36,0.8)]"
                : "bg-gradient-to-br from-teal-500 to-teal-700 shadow-[0_6px_20px_-6px_rgba(20,184,166,0.8)]";
        return (
            <div
                className={`
                    shrink-0 w-12 h-12 flex items-center justify-center rounded-xl text-white
                    ${toneClass}
                    transition-transform duration-500 group-hover:scale-105 group-hover:rotate-3
                `}
            >
                <Icon className="w-6 h-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]" />
            </div>
        );
    };

    return (
        <div className="grid gap-4 sm:gap-5 gap-y-4 md:grid-cols-2 xl:grid-cols-4">

            {/* ============ Total Plan Tokens ============ */}
            <CardShell>
                <div className="relative w-full flex justify-between items-start gap-3 p-4 sm:p-5">
                    <div className="min-w-0">
                        <p className="text-slate-500 dark:!text-slate-400 text-xs sm:text-sm font-medium tracking-wide">
                            {t("statisticsCards.totalCharge")}
                        </p>
                        <p className="text-lg sm:text-xl font-normal text-slate-900 dark:!text-slate-100 mt-1">
                            {botInfoLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <strong className={`text-slate-900 dark:!text-amber-100 font-bold tabular-nums ${isEnglish && 'font-sans'}`}>
                                        {planTokens.toLocaleString("en-US")}
                                    </strong>
                                    <span> </span>
                                    <span className="text-xs sm:text-sm text-slate-500 dark:!text-slate-400 mr-1">
                                        {t("statisticsCards.token")}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                    <IconBox icon={Zap} tone="gold" />
                </div>

                <div
                    className="
                        relative border-t border-teal-500/10 dark:!border-teal-400/10
                        px-4 sm:px-5 py-3 flex justify-between items-center w-full
                        transition-colors duration-300
                        group/row
                    "
                >
                    <span className="absolute top-0 right-0 h-[1px] w-16 bg-gradient-to-l from-amber-400/50 to-transparent" />
                    {botInfoLoading ? (
                        <Loader />
                    ) : (
                        <p className="text-teal-700 dark:!text-amber-200 text-sm sm:text-base font-bold truncate">
                            {planName || "—"}
                        </p>
                    )}

                    <button
                        type="button"
                        aria-label={t("statisticsCards.buyPlan")}
                        onClick={() => navigate("/user/plan")}
                        className="
                            relative shrink-0
                            flex items-center justify-center
                            h-9 w-9 rounded-lg
                            text-teal-700 dark:!text-amber-200
                            bg-white/40 dark:!bg-white/5
                            border border-teal-500/20 dark:!border-teal-400/20
                            hover:border-amber-300/60 hover:text-amber-600 dark:!hover:text-amber-200
                            hover:bg-amber-300/10
                            backdrop-blur-md
                            transition-all duration-300
                            hover:scale-105 active:scale-95
                        "
                    >
                        <ShoppingCart className="h-4 w-4" />
                        <span
                            className="
                                absolute bottom-full mb-2 left-1/2 -translate-x-1/2
                                whitespace-nowrap rounded-md
                                bg-slate-900/95 dark:!bg-black/90
                                text-white text-[10px] font-medium
                                py-1 px-2
                                opacity-0 group-hover/row:opacity-100 translate-y-1 group-hover/row:translate-y-0
                                transition-all duration-200 pointer-events-none
                                shadow-lg
                                border border-amber-300/30
                            "
                        >
                            {t("statisticsCards.buyPlan")}
                        </span>
                    </button>
                </div>
            </CardShell>

            {/* ============ Wallet ============ */}
            <CardShell>
                <div className="relative w-full flex justify-between items-start gap-3 p-4 sm:p-5">
                    <div className="min-w-0">
                        <p className="text-slate-500 dark:!text-slate-400 text-xs sm:text-sm font-medium tracking-wide">
                            {t("statisticsCards.wallet")}
                        </p>
                        <p className="text-lg sm:text-xl font-normal text-slate-900 dark:!text-slate-100 mt-1">
                            {botInfoLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <strong className={`text-slate-900 dark:!text-amber-100 font-bold tabular-nums ${isEnglish && 'font-sans'}`}>
                                        {wallet?.toLocaleString?.("en-US")}
                                    </strong>
                                    <span> </span>
                                    <span className="text-xs sm:text-sm text-slate-500 dark:!text-slate-400 mr-1">
                                        {t("statisticsCards.rial")}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                    <IconBox icon={Wallet} tone="teal" />
                </div>

                <button
                    type="button"
                    onClick={() => navigate("/wallet")}
                    className="
                        relative w-full
                        border-t border-teal-500/10 dark:!border-teal-400/10
                        px-4 sm:px-5 py-3
                        flex justify-between items-center
                        text-teal-700 dark:!text-amber-200
                        hover:bg-gradient-to-l hover:from-amber-300/15 hover:to-teal-400/10
                        transition-all duration-300 group/row
                    "
                >
                    <span className="absolute top-0 right-0 h-[1px] w-16 bg-gradient-to-l from-amber-400/50 to-transparent" />
                    <span className="text-sm sm:text-base font-bold group-hover/row:tracking-wide transition-all duration-300">
                        {t("statisticsCards.addCredit")}
                    </span>
                    <WalletIcon className="w-5 h-5 text-teal-700 dark:!text-amber-200 transition-transform duration-300 group-hover/row:scale-110" />
                </button>
            </CardShell>

            {/* ============ Dynamic Cards (Tokens / Days) ============ */}
            {statisticsCardsData.map((data, index) => {
                const Icon = data.icon;
                return (
                    <CardShell key={index}>
                        <div className="relative w-full flex justify-between items-start gap-3 p-4 sm:p-5">
                            <div className="min-w-0">
                                <p className="text-slate-500 dark:!text-slate-400 text-xs sm:text-sm font-medium tracking-wide">
                                    {data?.name}
                                </p>
                                <p className="text-lg sm:text-xl font-normal text-slate-900 dark:!text-slate-100 mt-1">
                                    {botInfoLoading ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <strong className={`text-slate-900 dark:!text-amber-100 font-bold tabular-nums ${isEnglish && 'font-sans'}`}>
                                                {typeof data?.value === "number"
                                                    ? data.value.toLocaleString("en-US")
                                                    : data?.value}
                                            </strong>
                                            <span> </span>
                                            <span className="text-xs sm:text-sm text-slate-500 dark:!text-slate-400 mr-1">
                                                {data?.unit}
                                            </span>
                                        </>
                                    )}
                                </p>
                            </div>
                            <IconBox icon={Icon} tone="teal" />
                        </div>

                        <div className="border-t border-teal-500/10 dark:!border-teal-400/10 px-4 sm:px-5 py-3">
                            <div className="w-full">
                                {botInfoLoading ? (
                                    <Loader />
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <p className="text-slate-500 dark:!text-slate-400 text-[11px] font-medium">
                                                {t("statisticsCards.usageRate")}
                                            </p>
                                            <p className={`text-slate-700 dark:!text-amber-100 text-xs font-bold tabular-nums ${isEnglish && 'font-sans'}`}>
                                                {data?.completion}%
                                            </p>
                                        </div>
                                        <div className="w-full h-1.5 rounded-full bg-slate-200/70 dark:!bg-slate-800/70 overflow-hidden">
                                            <div
                                                className={`
                                                    h-full rounded-full
                                                    bg-gradient-to-l ${progressClass(Number(data?.completion))}
                                                    transition-[width] duration-700 ease-out
                                                `}
                                                style={{ width: `${data.completion}%` }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardShell>
                );
            })}
        </div>
    );
}